import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { UserCircle2 } from "lucide-react";

/**
 * Warm household-member picker. Fetches ``GET /household/members`` once and
 * renders a select styled to match the app's input-warm look. Emits
 * ``onChange(assignee_id | null)`` — pass ``null`` for "Unassigned".
 *
 * Props:
 *   value         — currently selected member id (string) or empty
 *   onChange      — setter (id | null)
 *   label         — optional caption above the field (default "Assign to")
 *   testId        — data-testid for the trigger (default "assignee-picker")
 *   disabled
 */
export default function AssigneePicker({
  value,
  onChange,
  label = "Assign to",
  testId = "assignee-picker",
  disabled,
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        const { data } = await api.get("/household/members");
        if (!stopped) setMembers(Array.isArray(data) ? data : (data?.members || []));
      } catch {
        if (!stopped) setMembers([]);
      } finally {
        if (!stopped) setLoading(false);
      }
    })();
    return () => { stopped = true; };
  }, []);

  return (
    <label className="block">
      <span className="caption text-hearth-mute tracking-widest inline-flex items-center gap-1.5">
        <UserCircle2 size={12} /> {label}
      </span>
      <select
        data-testid={testId}
        disabled={disabled || loading}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="input-warm mt-1.5 h-11 w-full rounded-2xl border border-hearth-line bg-hearth-cream/40 px-3 focus:outline-none focus:border-hearth-clay/60 disabled:opacity-60"
      >
        <option value="">Unassigned</option>
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name || m.email}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Small pill/badge showing who a task is assigned to. Given an
 * ``assignee_id`` and the household members array, renders a warm chip.
 * Renders ``null`` when unassigned so the row stays clean.
 */
export function AssigneeChip({ assigneeId, members = [], size = "sm" }) {
  if (!assigneeId) return null;
  const m = members.find((x) => x.id === assigneeId);
  if (!m) return null;
  const initials = (m.name || m.email || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const px = size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1";
  return (
    <span
      data-testid={`assignee-chip-${assigneeId}`}
      className={`inline-flex items-center gap-1.5 rounded-full bg-hearth-sage/15 text-hearth-sageDark ${px} font-medium`}
      title={`Assigned to ${m.name || m.email}`}
    >
      <span className="w-4 h-4 rounded-full bg-hearth-sageDark/25 grid place-items-center text-[8px] font-semibold">
        {initials}
      </span>
      <span className="truncate max-w-[100px]">{m.name || m.email.split("@")[0]}</span>
    </span>
  );
}
